import type { MoneyInput } from '@agos/shared'

/**
 * What a vehicle costs to run, and when its paperwork runs out.
 *
 * The expiry bands are the part that matters legally: driving on a lapsed STK or
 * without insurance is an offence, and the fleet page is the only place anybody
 * sees the date. Thirty days' warning is what legacy gives, and it is enough to
 * book a test.
 *
 * Ported from legacy `pages/vehicle-fleet.tsx`, with the fuel arithmetic in
 * integer cents (§14.7).
 */

export type ExpiryStatus = 'valid' | 'soon' | 'expired' | 'none'

/** A month's notice — long enough to book an STK slot. */
const SOON_DAYS = 30
const MS_PER_DAY = 86_400_000

export const EXPIRY_CLASSES: Record<ExpiryStatus, string> = {
  valid: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  soon: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  expired: 'bg-red-500/10 text-red-600 border-red-500/20',
  none: 'bg-muted text-muted-foreground border-border',
}

/**
 * `none` and `expired` are deliberately different answers.
 *
 * A vehicle with no date recorded is not compliant — nobody knows — but it is
 * also not *known* to be illegal, and a manager needs to tell the two apart to
 * know whether to chase the paperwork or take the car off the road.
 */
export function expiryStatus(date: string | null | undefined, now: number): ExpiryStatus {
  if (!date) return 'none'
  const expiry = new Date(date).getTime()
  if (!Number.isFinite(expiry)) return 'none'

  // Ceil, so a certificate expiring later today still reads as valid — it is.
  const daysLeft = Math.ceil((expiry - now) / MS_PER_DAY)
  if (daysLeft < 0) return 'expired'
  if (daysLeft <= SOON_DAYS) return 'soon'
  return 'valid'
}

export interface FleetVehicle {
  /** Litres per 100 km, as the API sends it. */
  fuelConsumption?: MoneyInput
  /** Euros per litre. */
  fuelPrice?: MoneyInput
  /** Kilometres driven on deliveries — the whole history, or the filtered range. */
  totalDeliveryKm?: number
}

/** `l/100km × km ÷ 100`. Litres are a measurement, not money, so they stay real. */
export function litresUsed(vehicle: FleetVehicle): number {
  const perHundred = Number(vehicle.fuelConsumption ?? 0)
  if (!Number.isFinite(perHundred)) return 0
  return (perHundred / 100) * (vehicle.totalDeliveryKm ?? 0)
}

/**
 * What the fuel cost, in cents.
 *
 * The per-litre price is **not** put through `parseMoneyToCents`. Slovak pumps
 * quote three decimals — 1,499 €/l — and a two-decimal parser rounds that to
 * 1,50 €, which over a month's driving is euros of invented cost. A unit price
 * is not a payable amount; only the product is, and that is what gets rounded.
 */
export function fuelCostCents(vehicle: FleetVehicle): number {
  const pricePerLitre = Number(vehicle.fuelPrice ?? 0)
  if (!Number.isFinite(pricePerLitre) || pricePerLitre <= 0) return 0
  const total = litresUsed(vehicle) * pricePerLitre * 100
  return total < 0 ? -Math.round(-total) : Math.round(total)
}

export type FuelType = 'benzin' | 'nafta' | 'lpg_benzin'

export const FUEL_TYPE_CLASSES: Record<string, string> = {
  nafta: 'bg-stone-500/10 text-stone-600 border-stone-500/20',
  lpg_benzin: 'bg-green-500/10 text-green-600 border-green-500/20',
  benzin: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
}

/**
 * The odometer a driver would read off the dashboard: what it said when the
 * vehicle was entered, plus everything driven on deliveries since.
 *
 * The API sends this as `currentOdometer` and it is trusted when present —
 * private mileage is in it too, and the frontend cannot know about that.
 */
export function odometerReading(vehicle: {
  currentOdometer?: number
  odometer?: number
  deliveryKm?: number
}): number {
  return vehicle.currentOdometer ?? (vehicle.odometer ?? 0) + (vehicle.deliveryKm ?? 0)
}

/**
 * A vehicle's papers, in the order the card draws them.
 *
 * The vignette is the fourth — §10A.82, and legacy has no such column, so this is a new field rather
 * than a missed port.
 */
export interface VehicleDocuments {
  stkExpiry?: string | null
  ekExpiry?: string | null
  insuranceExpiry?: string | null
  vignetteExpiry?: string | null
  active?: boolean
}

function documentDates(vehicle: VehicleDocuments): (string | null | undefined)[] {
  return [vehicle.stkExpiry, vehicle.ekExpiry, vehicle.insuranceExpiry, vehicle.vignetteExpiry]
}

/** The worst state across a vehicle's four documents — what the list sorts on. */
export function worstExpiry(vehicle: VehicleDocuments, now: number): ExpiryStatus {
  const order: ExpiryStatus[] = ['expired', 'soon', 'none', 'valid']
  const states = documentDates(vehicle).map((date) => expiryStatus(date, now))
  return order.find((candidate) => states.includes(candidate)) ?? 'valid'
}

/** How many documents are already lapsed, and how many run out inside the month. */
export interface FleetWarnings {
  expired: number
  soon: number
}

/**
 * What the page says out loud above the cards — §9.205.
 *
 * **Lapsed and lapsing are counted apart and never summed.** *Two vehicles cannot legally drive* and
 * *two need booking this month* are different instructions, to different people, on different days;
 * one number covering both is one nobody acts on.
 *
 * **Documents, not vehicles.** A van with a lapsed STK and a lapsed vignette is two bookings at two
 * places, which is what the count is for.
 *
 * **Inactive vehicles are skipped**, matching the nightly check (§10A.72), which filters
 * `active = true`. A van off the road with a lapsed STK is not a warning; it is why it is off the
 * road. `none` is skipped too: a date nobody recorded is not a date that has passed.
 */
export function fleetWarnings(vehicles: VehicleDocuments[], now: number): FleetWarnings {
  let expired = 0
  let soon = 0
  for (const vehicle of vehicles) {
    if (vehicle.active === false) continue
    for (const date of documentDates(vehicle)) {
      const status = expiryStatus(date, now)
      if (status === 'expired') expired += 1
      else if (status === 'soon') soon += 1
    }
  }
  return { expired, soon }
}
